import React, { Fragment } from 'react'
import { FormattedMessage, FormattedNumber } from 'react-intl'
import { Link } from 'react-router-dom'
import { localizeLink } from '../../utils'
import messages from './messages'
import FormattedCurrency from '../FormattedCurrency'
import { useSelector } from 'react-redux'
import { selectIsPremium, selectIsLoggedIn } from '../../store/userSlice'
import {
  selectPricingConfig,
  getPlanWithLowestMonthlyPrice,
} from '../../store/pricingSlice'
import { trackStartRegistration } from '../../analytics'
import cn from 'classnames'
import useLocale from 'js/hooks/useLocale'

type Plan = {
  duration: number
  pricePerMonth: number
  originalPrice: number
}

type FromPriceProps = {
  absoluteFromPrice?: boolean
  cheapestPlan?: Plan
  discount: number
  currency: string
  discountType: string
  priceInside?: boolean
  small?: boolean
}

export const FromPrice = ({
  absoluteFromPrice = false,
  discount,
  cheapestPlan,
  currency,
  discountType,
  priceInside,
  small,
}: FromPriceProps) => {
  if (small) return null
  const classNamesObject = {
    'c-premium-button__price-inside mb-0': priceInside,
    'c-premium-button__from-price whitespace-nowrap': !priceInside,
    'c-premium-button__from-price--absolute': absoluteFromPrice,
  }

  if (!cheapestPlan) {
    return <p className={cn('invisible', classNamesObject)}>placeholder</p>
  }

  return (
    <p className={cn(classNamesObject)} key={cheapestPlan.duration}>
      {!discount && (
        <Fragment>
          <FormattedMessage {...messages.from} />{' '}
          <FormattedCurrency
            currency={currency}
            value={cheapestPlan.pricePerMonth}
          />
          {'/'}
          {priceInside ? (
            <FormattedMessage {...messages.shortMon} />
          ) : (
            <FormattedMessage {...messages.month} />
          )}
        </Fragment>
      )}
      {!!discount && cheapestPlan.originalPrice && (
        <Fragment>
          {priceInside && (
            <Fragment>
              <FormattedMessage {...messages.from} />{' '}
            </Fragment>
          )}
          <del className="strikethrough">
            <FormattedCurrency
              currency={currency}
              value={
                Math.round(
                  (cheapestPlan.originalPrice / cheapestPlan.duration) * 100,
                ) / 100
              }
              round
            />
          </del>{' '}
          <ins>
            <FormattedCurrency
              currency={currency}
              value={cheapestPlan.pricePerMonth}
              round
            />
          </ins>
          {'/'}
          {priceInside ? (
            <FormattedMessage {...messages.shortMon} />
          ) : (
            <FormattedMessage {...messages.month} />
          )}
        </Fragment>
      )}
      {!!discount && !priceInside && (
        <Fragment>
          {' — '}
          <FormattedMessage {...messages.save} />{' '}
          <b>
            {discountType === '%' ? (
              <FormattedNumber value={discount / 100} style="percent" />
            ) : (
              <FormattedNumber
                value={discount}
                currency={currency}
                style="currency"
                maximumFractionDigits={2}
              />
            )}
          </b>
        </Fragment>
      )}
    </p>
  )
}

type Props = {
  GALabel: string
  marginless?: boolean
  absoluteFromPrice?: boolean
  signUpCopyBlock?: boolean
  signupCopyShadowBlack?: boolean
  onClickFunc?: () => void
  newStartpage?: boolean
  priceInside?: boolean
  plansPage?: boolean
  inlineBlock?: boolean
  small?: boolean
}

const PremiumButton = ({
  absoluteFromPrice,
  marginless = false,
  signUpCopyBlock = false,
  signupCopyShadowBlack = false,
  priceInside = false,
  onClickFunc,
  small,
  inlineBlock,
}: Props) => {
  const { language } = useLocale()
  const handleClick = (event) => {
    trackStartRegistration()

    if (onClickFunc) {
      event.preventDefault()
      onClickFunc()
    }
  }
  const config = useSelector(selectPricingConfig())
  const isLoggedIn = useSelector(selectIsLoggedIn)
  const isPremium = useSelector(selectIsPremium)
  const cheapestPlan = config?.products
    ? getPlanWithLowestMonthlyPrice(config.products)
    : undefined
  const discount = config?.discount
  const currency = config?.currency
  const trial = config?.trialDuration
  const discountType = config?.discountType

  const fromPricingProps = {
    absoluteFromPrice,
    discount,
    currency,
    discountType,
    cheapestPlan,
    priceInside,
    small,
  }

  if (isPremium) {
    return null
  }

  return (
    <div
      className={cn('c-premium-button', {
        'c-premium-button--inline-block': inlineBlock,
        'c-premium-button--marginless': marginless,
      })}
      data-uid="PremiumButton"
    >
      <Link
        to={
          isLoggedIn
            ? localizeLink('/upgrade/', language)
            : localizeLink('/signup/?premium=1', language)
        }
        className={cn(
          'c-button c-button--premium c-premium-button__cta c-premium-button__cta--signup',
          {
            'c-button--shadow-black': signupCopyShadowBlack,
            'c-button--block': signUpCopyBlock,
            'c-premium-button--price-inside': priceInside,
            'c-button--small': small,
          },
        )}
        onClick={handleClick}
      >
        <span>
          {isLoggedIn ? (
            <>
              <FormattedMessage {...messages.upgradeTo} />
              {' Premium'}
              {priceInside && <FromPrice {...fromPricingProps} />}
            </>
          ) : trial ? (
            <FormattedMessage
              id="trial_tryForFree"
              defaultMessage="Try for free"
            >
              {([text]) => (
                <span>
                  {(text as string).replace('Try', `Try ${trial} days`)}
                </span>
              )}
            </FormattedMessage>
          ) : (
            <>
              <FormattedMessage {...messages.signUp} />
              {priceInside && <FromPrice {...fromPricingProps} />}
            </>
          )}
        </span>
      </Link>
      {!priceInside && !trial && <FromPrice {...fromPricingProps} />}
    </div>
  )
}

export default PremiumButton
